import { Checklist } from "@common/schemas/checklist";
import { autorun, makeAutoObservable } from "mobx";
import { ChecklistManagerEvents, ChecklistManagerNamespace } from "./checklistManager.events";
import { EventEmitter } from "../events/eventEmitter";
import { ChecklistScheduleReason } from "../scheduler/scheduler";
import { ChecklistStateStorage } from "../tour/persistent-storage/checklistState";
import { UserStateStorage } from "../tour/persistent-storage/userState";
import { ChecklistTerminateReason } from "../tour/tourLifecycleManager";

export type ChecklistManagerParams = {
  checklist: Checklist;
  checklistState: ChecklistStateStorage;
  userState: UserStateStorage;
  isPreview: boolean;
};

export type ChecklistCompletionState = Array<{ guide_id: string; completed: boolean }>;

export class ChecklistManager {
  events = new EventEmitter<typeof ChecklistManagerNamespace, ChecklistManagerEvents>(ChecklistManagerNamespace);
  checklist: Checklist;
  checklistState: ChecklistStateStorage;
  userState: UserStateStorage;
  isPreview = false;
  isVisibleTogetherWithTour = false;

  constructor({ checklist, checklistState, userState, isPreview }: ChecklistManagerParams) {
    makeAutoObservable(this);
    this.checklist = checklist;
    this.checklistState = checklistState;
    this.userState = userState;
    this.isPreview = isPreview;

    if (!this.isPreview) {
      autorun(() => {
        if (this.checklist.items.every((item) => this.userState.finishedTourGuideIds.includes(item.guide_id))) {
          this.completeChecklist();
        }
      });
    }
  }

  startChecklist(scheduleReason: ChecklistScheduleReason) {
    this.checklistState.setActiveChecklistId(this.checklist.id);
    this.events.emit("checklistStarted", { checklist: this.checklist, scheduleReason });
  }

  dismissChecklist() {
    this.userState.addDismissedChecklist(this.checklist.id);
    this.checklistState.clearActiveChecklistId();
    this.events.emit("checklistDismissed", { checklist: this.checklist });
  }

  minimizeChecklist() {
    this.checklistState.setIsMinimized(true);
  }

  expandChecklist() {
    this.checklistState.setIsMinimized(false);
  }

  completeChecklist() {
    this.events.emit("checklistCompleted", { checklist: this.checklist });
  }

  terminateChecklist(reason: ChecklistTerminateReason) {
    this.events.emit("checklistTerminated", { checklist: this.checklist, reason });
  }

  setIsVisibleTogetherWithTour(isVisible: boolean) {
    this.isVisibleTogetherWithTour = isVisible;
  }

  get currentCompletionState(): ChecklistCompletionState {
    return this.checklist.items.map((item) => ({
      guide_id: item.guide_id,
      completed: this.userState.finishedTourGuideIds.includes(item.guide_id),
    }));
  }

  get isMinimized() {
    return this.checklistState.isMinimized;
  }
}
